import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, Mail, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';
import { toast } from 'sonner';
import { requestPasswordReset, performPasswordReset, setToken } from '../lib/api';
import { useApp } from '../context/AppContext';

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  return token ? <PerformReset token={token} /> : <RequestReset />;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[var(--theme-bg)] text-[var(--theme-fg)] flex items-center justify-center p-4">
      <div className="w-full max-w-[460px] card-shell p-7 md:p-8">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-[12.5px] text-white/60 hover:text-white mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Back to login</Link>
        <div className="flex items-center gap-2 mb-4"><Logo /></div>
        {children}
      </div>
    </div>
  );
}

function RequestReset() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Enter your email'); return; }
    setBusy(true);
    try { await requestPasswordReset(email); setSent(true); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not send reset link'); }
    finally { setBusy(false); }
  };

  return (
    <Shell>
      {sent ? (
        <div className="text-center py-4" data-testid="forgot-sent">
          <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-3" style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }}>
            <CheckCircle2 className="w-7 h-7 text-black" />
          </div>
          <h1 className="font-display text-[26px]">Check your inbox</h1>
          <p className="text-white/60 text-[13.5px] mt-2 max-w-[360px] mx-auto">If that email is on file, we just sent a reset link. It expires in 60 minutes.</p>
          <Link to="/login" className="btn-ghost mt-5 inline-flex text-[12.5px]">Back to sign in</Link>
        </div>
      ) : (
        <>
          <h1 className="font-display text-[28px] leading-none">Forgot your password?</h1>
          <p className="text-white/60 text-[13.5px] mt-2">No stress. Drop your email and we'll send you a one-time reset link.</p>
          <form onSubmit={submit} className="mt-5 space-y-3">
            <label className="block">
              <span className="block text-[11.5px] uppercase tracking-wider text-white/55 mb-1 font-semibold">Email</span>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input-field w-full" placeholder="you@reapercast.io" data-testid="forgot-email" autoFocus />
            </label>
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="forgot-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email me a reset link
            </button>
          </form>
        </>
      )}
    </Shell>
  );
}

function PerformReset({ token }) {
  const nav = useNavigate();
  const { refreshMe } = useApp();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (pwd !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const r = await performPasswordReset({ token, new_password: pwd });
      if (r?.token) setToken(r.token);
      await refreshMe();
      toast.success('Password reset. Welcome back.');
      nav('/home');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired reset link');
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <h1 className="font-display text-[28px] leading-none">Set a new password</h1>
      <p className="text-white/60 text-[13.5px] mt-2">Pick something strong — you'll be signed in automatically.</p>
      <form onSubmit={submit} className="mt-5 space-y-3">
        <label className="block">
          <span className="block text-[11.5px] uppercase tracking-wider text-white/55 mb-1 font-semibold">New password</span>
          <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} className="input-field w-full" data-testid="reset-pwd" autoFocus autoComplete="new-password" />
        </label>
        <label className="block">
          <span className="block text-[11.5px] uppercase tracking-wider text-white/55 mb-1 font-semibold">Confirm</span>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="input-field w-full" data-testid="reset-confirm" autoComplete="new-password" />
        </label>
        <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="reset-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Set new password
        </button>
      </form>
    </Shell>
  );
}
